// ===========================================
// Calendar Cancel Occurrence Dialog
// ===========================================

import { format } from 'date-fns';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Typography,
  TextField,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { CalendarEvent } from '../../../api/hybridBooking.api';

interface CancelDialogState {
  open: boolean;
  event: CalendarEvent | null;
  reason: string;
  notifyParents: boolean;
}

interface CalendarCancelDialogProps {
  dialog: CancelDialogState;
  isPending: boolean;
  onReasonChange: (reason: string) => void;
  onNotifyParentsChange: (value: boolean) => void;
  onConfirm: () => void;
  onCancel: () => void;
}

export default function CalendarCancelDialog({
  dialog,
  isPending,
  onReasonChange,
  onNotifyParentsChange,
  onConfirm,
  onCancel,
}: CalendarCancelDialogProps) {
  return (
    <Dialog open={dialog.open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>Cancel This Class</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography>
            Cancel <strong>{dialog.event?.title}</strong> on{' '}
            {dialog.event && format(new Date(dialog.event.start), 'EEEE, d MMM')}?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Only this occurrence will be cancelled. All other weeks will be unaffected.
          </Typography>
          <TextField
            label="Reason (optional)"
            multiline
            rows={2}
            value={dialog.reason}
            onChange={(e) => onReasonChange(e.target.value)}
            placeholder="e.g., Public holiday, Teacher unavailable"
            fullWidth
            inputProps={{ maxLength: 500 }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={dialog.notifyParents}
                onChange={(e) => onNotifyParentsChange(e.target.checked)}
              />
            }
            label="Notify parents via email"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={isPending}>
          Back
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={onConfirm}
          disabled={isPending}
        >
          {isPending ? 'Cancelling...' : 'Cancel Class'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
